export const getLocalTimezone = (): string => {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// example - return 2020-10-05T00:00:00
export const getLocalDateTimeStartOfDay = (date: Date) => {
  const localDate = getLocalDateFromDateTime(date);
  return `${localDate}T00:00:00`;
};

export const convertDateToRfcStartOfDay = (date: Date) => {
  const localDate = getLocalDateFromDateTime(date);
  return `${localDate}T00:00:00.000Z`;
};

export const getLocalDateFromDateTime = (date: Date) => {
  const addZero = (num: number) => (num < 10 ? `0${num}` : `${num}`);
  return `${date.getFullYear()}-${addZero(date.getMonth() + 1)}-${addZero(
    date.getDate(),
  )}`;
};

export const getDateTimeFromLocalDateTime = (localDateTime: string) => {
  return new Date(localDateTime);
};

export const formtLocalDateTimeToDateView = (
  localDateTime: string,
  locale?: string,
) => {
  const date = new Date(localDateTime);
  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    weekday: 'long',
    month: 'long',
  };
  return formatter(date, formatOptions, locale);
};

export const formatRfcTimeStringToTimeSlotView = (
  rfcTime: string,
  locale?: string,
) => {
  const date = new Date(convertRfcTimeToLocalDateTime(rfcTime));
  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  };
  return formatter(date, formatOptions, locale).toLowerCase();
};

export const formatShortDate = (
  dateString: string,
  locale?: string,
  timezone?: string,
) => {
  return formatter(
    new Date(dateString),
    {
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    } as Intl.DateTimeFormatOptions,
    locale,
  );
};

export const formatShortTime = (
  dateString: string,
  locale?: string,
  timezone?: string,
) => {
  return formatter(
    new Date(dateString),
    {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: timezone,
    } as Intl.DateTimeFormatOptions,
    locale,
  );
};

export const formatRfcTimeStringToDateAndTimeView = (
  rfcTime: string,
  locale: string,
) => {
  const formattedDate = formatRfcTimeToDateView({
    rfcTime,
    locale,
  });
  const formattedTime = formatRfcTimeStringToTimeSlotView(rfcTime, locale);
  return `${formattedDate}, ${formattedTime}`;
};

export const formatRfcTimeToDateView = ({
  rfcTime,
  locale,
}: {
  rfcTime: string;
  locale?: string;
}) => {
  const date = new Date(convertRfcTimeToLocalDateTime(rfcTime));
  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    year: 'numeric',
    month: 'long',
  };
  return formatter(date, formatOptions, locale);
};

const convertRfcTimeToLocalDateTime = (rfcTime: string) =>
  rfcTime.substr(0, rfcTime.indexOf('.'));

const formatter = (
  date: Date,
  formatOptions: Intl.DateTimeFormatOptions,
  locale?: string,
) => {
  const dateTimeFormat = new Intl.DateTimeFormat(locale, formatOptions);
  return dateTimeFormat.format(date);
};

export const minutesDifferencesBetweenTwoDates = (
  rfcStartTime: string,
  rfcEndTime: string,
) => {
  const secondsInDay = 86400;
  const minutesInHour = 60;
  const hoursInDay = 24;
  const delta =
    Math.abs(
      new Date(rfcStartTime).getTime() - new Date(rfcEndTime).getTime(),
    ) / 1000;

  const days = Math.floor(delta / secondsInDay);
  const hours =
    Math.floor(delta / (minutesInHour * minutesInHour)) % hoursInDay;
  const minutes = Math.floor(delta / minutesInHour) % minutesInHour;

  return minutes + hours * minutesInHour + days * hoursInDay;
};

export function getTodayLocalDateTimeStartOfDay(timezone: string): string {
  const dateTimeFormat = new Intl.DateTimeFormat('en-us', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  });
  const dateString = dateTimeFormat.format(new Date());
  const shiftedDate = new Date(dateString);

  return getLocalDateTimeStartOfDay(shiftedDate);
}

export function getStartOfMonthAsLocalDateTime(localDateTime: string): string {
  const date = new Date(localDateTime);
  date.setDate(1);
  return getLocalDateTimeStartOfDay(date);
}

export function getEndOfMonth(
  localDateTime: string,
  monthRange: number,
): string {
  const date = new Date(localDateTime);
  const endOfMonthAsDate = new Date(
    date.getFullYear(),
    date.getMonth() + monthRange,
    0,
  );
  const endOfMonthAsLocalDate = getLocalDateFromDateTime(endOfMonthAsDate);
  return getEndOfDayFromLocalDate(endOfMonthAsLocalDate);
}

export function getEndOfDayFromLocalDateTime(localDateTime: string): string {
  const localDate = localDateTime.substr(0, localDateTime.indexOf('T'));
  return getEndOfDayFromLocalDate(localDate);
}

export function getEndOfDayFromLocalDate(localDate: string): string {
  return `${localDate}T23:59:59`;
}

export function convertRfcTimeToLocalDateTimeStartOfDay(
  rfcTime: string,
): string {
  const localDate = rfcTime.substr(0, rfcTime.indexOf('T'));
  return `${localDate}T00:00:00`;
}

export function isMonthDifferent(
  localDateTime1: string,
  localDateTime2: string,
) {
  return (
    new Date(localDateTime1).getMonth() !== new Date(localDateTime2).getMonth()
  );
}
